/**
 * logs-tab.tsx — Tab "Nhật ký": Hoạt động (activity_logs), Đăng nhập (login_logs),
 * Quản trị Platform (platform_audit_logs nhắm vào tổ chức). Lọc người/hành động/ngày,
 * phân trang, xuất CSV (tối đa 10.000 dòng, theo bộ lọc đang chọn).
 */
import { useState } from 'react'
import { Download, Loader2 } from 'lucide-react'
import { toast } from 'sonner'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { DataTable, type Column } from '@/components/shared/data-table'
import { Pagination } from '@/components/shared/pagination'
import { ErrorState } from '@/components/shared/feedback'
import { platformApiError } from '@/lib/platform-client'
import {
  downloadOrgLogCsv, useOrgLogFilters, useOrgLogs,
  type ActivityLogItem, type LogKind, type LogQuery, type LoginLogItem, type PlatformLogItem,
} from '@/hooks/use-platform-org-manage'
import { fmtDateTime, platformActionLabel } from './labels'

const LIMIT = 20
const ALL = '__all__'

export function LogsTab({ orgId }: { orgId: string }) {
  return (
    <Tabs defaultValue="activity">
      <TabsList>
        <TabsTrigger value="activity">Hoạt động</TabsTrigger>
        <TabsTrigger value="logins">Đăng nhập</TabsTrigger>
        <TabsTrigger value="platform">Quản trị Platform</TabsTrigger>
      </TabsList>
      <TabsContent value="activity"><LogPanel orgId={orgId} kind="activity" /></TabsContent>
      <TabsContent value="logins"><LogPanel orgId={orgId} kind="logins" /></TabsContent>
      <TabsContent value="platform"><LogPanel orgId={orgId} kind="platform" /></TabsContent>
    </Tabs>
  )
}

function LogPanel({ orgId, kind }: { orgId: string; kind: LogKind }) {
  const filters = useOrgLogFilters(orgId)
  const [f, setF] = useState({ userId: ALL, module: ALL, success: ALL, action: ALL, from: '', to: '' })
  const [page, setPage] = useState(1)
  const [exporting, setExporting] = useState(false)
  const set = (k: keyof typeof f, v: string) => { setF((x) => ({ ...x, [k]: v })); setPage(1) }
  const val = (v: string) => (v === ALL || v === '' ? undefined : v)

  const query: LogQuery = {
    from: val(f.from), to: val(f.to),
    ...(kind === 'activity' ? { userId: val(f.userId), module: val(f.module) } : {}),
    ...(kind === 'logins' ? { userId: val(f.userId), success: val(f.success) } : {}),
    ...(kind === 'platform' ? { action: val(f.action) } : {}),
  }
  const { data, isLoading, isError } = useOrgLogs<ActivityLogItem & LoginLogItem & PlatformLogItem>(orgId, kind, { ...query, page, limit: LIMIT })

  async function exportCsv() {
    setExporting(true)
    try {
      const info = await downloadOrgLogCsv(orgId, kind, query)
      // C13: vượt giới hạn → báo rõ đã cắt (dòng cuối file cũng ghi chú).
      if (info.truncated) {
        toast.warning(
          `Đã xuất ${info.max.toLocaleString('vi-VN')} dòng đầu (trên tổng ${info.total.toLocaleString('vi-VN')}), hãy lọc hẹp khoảng thời gian`,
          { duration: 10_000 },
        )
      }
    } catch (err) {
      toast.error(platformApiError(err))
    } finally {
      setExporting(false)
    }
  }

  const activityCols: Column<ActivityLogItem>[] = [
    { key: 'time', header: 'Thời gian', cell: (r) => <span className="whitespace-nowrap">{fmtDateTime(r.createdAt)}</span> },
    { key: 'actor', header: 'Người thao tác', cell: (r) => <span title={r.actor?.email}>{r.actorLabel}</span> },
    { key: 'module', header: 'Mô-đun', cell: (r) => <Badge variant="outline">{r.moduleLabel}</Badge> },
    { key: 'action', header: 'Thao tác', cell: (r) => r.actionLabel },
    { key: 'target', header: 'Đối tượng', cell: (r) => r.targetName ?? '—' },
    { key: 'detail', header: 'Chi tiết', cell: (r) => <span className="line-clamp-2 max-w-[320px] text-xs text-muted-foreground" title={r.detailText}>{r.detailText || '—'}</span> },
  ]
  const loginCols: Column<LoginLogItem>[] = [
    { key: 'time', header: 'Thời gian', cell: (r) => <span className="whitespace-nowrap">{fmtDateTime(r.createdAt)}</span> },
    { key: 'user', header: 'Nhân viên', cell: (r) => r.user ? <span title={r.user.email}>{r.user.fullName}</span> : 'Nhân viên đã xoá' },
    { key: 'result', header: 'Kết quả', cell: (r) => r.success ? <Badge variant="success">Thành công</Badge> : <Badge variant="destructive">{r.failureLabel ?? 'Thất bại'}</Badge> },
    { key: 'method', header: 'Cách', cell: (r) => r.method },
    { key: 'ip', header: 'IP', cell: (r) => <span className="font-mono text-xs">{r.ip ?? '—'}{r.ipChanged && <Badge variant="warning" className="ml-1">IP mới</Badge>}</span> },
    { key: 'device', header: 'Thiết bị', cell: (r) => [r.deviceLabel, r.browser, r.os].filter(Boolean).join(' · ') || '—' },
  ]
  const platformCols: Column<PlatformLogItem>[] = [
    { key: 'time', header: 'Thời gian', cell: (r) => <span className="whitespace-nowrap">{fmtDateTime(r.createdAt)}</span> },
    { key: 'admin', header: 'Quản trị viên', cell: (r) => <span title={r.admin?.email}>{r.admin?.fullName ?? '—'}</span> },
    { key: 'action', header: 'Hành động', cell: (r) => <Badge variant="outline">{platformActionLabel(r.action)}</Badge> },
    { key: 'user', header: 'Nhân viên', cell: (r) => r.targetUser?.fullName ?? (r.targetUserId ? 'Đã xoá' : '—') },
    { key: 'reason', header: 'Lý do', cell: (r) => r.reason ?? '—' },
    { key: 'meta', header: 'Chi tiết', cell: (r) => <MetaText meta={r.meta} /> },
  ]

  const users = filters.data?.users ?? []
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-end gap-2">
        {kind !== 'platform' && (
          <FilterSelect label="Nhân viên" value={f.userId} onChange={(v) => set('userId', v)}
            options={[{ value: ALL, label: 'Tất cả' }, ...users.map((u) => ({ value: u.id, label: u.fullName }))]} />
        )}
        {kind === 'activity' && (
          <FilterSelect label="Mô-đun" value={f.module} onChange={(v) => set('module', v)}
            options={[{ value: ALL, label: 'Tất cả' }, ...(filters.data?.modules ?? []).map((m) => ({ value: m.id, label: m.label }))]} />
        )}
        {kind === 'logins' && (
          <FilterSelect label="Kết quả" value={f.success} onChange={(v) => set('success', v)}
            options={[{ value: ALL, label: 'Tất cả' }, { value: 'true', label: 'Thành công' }, { value: 'false', label: 'Thất bại' }]} />
        )}
        {kind === 'platform' && (
          <FilterSelect label="Hành động" value={f.action} onChange={(v) => set('action', v)}
            options={[{ value: ALL, label: 'Tất cả' }, ...(filters.data?.platformActions ?? []).map((a) => ({ value: a, label: platformActionLabel(a) }))]} />
        )}
        <div className="space-y-1">
          <Label className="text-xs">Từ ngày</Label>
          <Input type="date" className="w-40" value={f.from} onChange={(e) => set('from', e.target.value)} />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Đến ngày</Label>
          <Input type="date" className="w-40" value={f.to} onChange={(e) => set('to', e.target.value)} />
        </div>
        <Button variant="outline" className="ml-auto" onClick={exportCsv} disabled={exporting}>
          {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />} Xuất CSV
        </Button>
      </div>

      {isError ? <ErrorState message="Không tải được nhật ký." /> : kind === 'activity' ? (
        <DataTable columns={activityCols} rows={(data?.items ?? []) as ActivityLogItem[]} loading={isLoading} rowKey={(r) => r.id} emptyTitle="Không có nhật ký" />
      ) : kind === 'logins' ? (
        <DataTable columns={loginCols} rows={(data?.items ?? []) as LoginLogItem[]} loading={isLoading} rowKey={(r) => r.id} emptyTitle="Không có nhật ký" />
      ) : (
        <DataTable columns={platformCols} rows={(data?.items ?? []) as PlatformLogItem[]} loading={isLoading} rowKey={(r) => r.id} emptyTitle="Không có nhật ký" />
      )}
      {data && data.total > 0 && <Pagination page={page} limit={LIMIT} total={data.total} onPageChange={setPage} />}
    </div>
  )
}

function FilterSelect({ label, value, onChange, options }: {
  label: string; value: string; onChange: (v: string) => void; options: Array<{ value: string; label: string }>
}) {
  return (
    <div className="space-y-1">
      <Label className="text-xs">{label}</Label>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className="w-44"><SelectValue /></SelectTrigger>
        <SelectContent>
          {options.map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  )
}

/** Tóm tắt meta (bỏ lý do — đã có cột riêng). */
function MetaText({ meta }: { meta: Record<string, unknown> }) {
  const { reason: _reason, ...rest } = meta
  const text = Object.keys(rest).length ? JSON.stringify(rest) : ''
  return <span className="line-clamp-2 max-w-[320px] font-mono text-[11px] text-muted-foreground" title={text}>{text || '—'}</span>
}
